/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useState, useEffect, useRef } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { Addon } from '../../types/shared/Addon';
import { User } from '../../types/shared/User';
import { createHexId } from '../../support/createHexId';
import { Account } from '../../types/shared/Account';
import AddonActions from '../../support/AddonActions';
import { canUser } from '../../support/shared/permissionHelpers';
import { showDialog } from '../App/AppDialog';

import LoadingIndicator from '../uiold/LoadingIndicator';
import {
  Text,
  TextInput,
  Icon,
  Link,
  Separator,
  Button,
  ButtonToolbar,
  CurrencyInput,
} from '../uiold';
import { themeColors } from '../uiold/Constants';

function AddonsList(props: {
  account: Account;
  addons: Map<string, Addon> | null | undefined;
  isLoading: boolean;
  user: User;
  saveAddons: () => Promise<ObjectOf<JSONValue>>;
}) {
  let { isLoading, addons, user, saveAddons, account } = props;
  let canEdit = canUser(user, { marketsAndPricing: 'WRITE' });
  let [addonsArray, setAddonsArray] = useState<Array<Addon>>(
    addons ? Array.from(addons?.values()) : [],
  );
  let isDirty = useRef(false);
  useEffect(() => {
    if (addonsArray[addonsArray.length - 1]?.name !== '') {
      let now = new Date().toISOString();
      let newAddon: Addon = {
        id: createHexId(),
        name: '',
        defaultPrice: 0,
        created_at: now,
        modified_at: now,
      };
      setAddonsArray([...addonsArray, newAddon]);
    }
  }, [addonsArray]);
  let onChangeAddon = (newAddon: Addon) => {
    let newAddonsArray = addonsArray.map((existingAddon) => {
      return existingAddon.id === newAddon.id
        ? { ...newAddon, modified_at: new Date().toISOString() }
        : existingAddon;
    });
    setAddonsArray(newAddonsArray);
    isDirty.current = true;
  };
  let commitSaveAddons = () => {
    if (
      addonsArray.length > 0 &&
      addonsArray[addonsArray.length - 1]?.name === ''
    ) {
      let newAddons = [...addonsArray].splice(0, addonsArray.length - 1);
      AddonActions.saveAddons(newAddons);
    } else {
      AddonActions.saveAddons(addonsArray);
    }
    saveAddons();
    isDirty.current = false;
  };
  let revertChanges = () => {
    setAddonsArray(addons ? Array.from(addons?.values()) : []);
    isDirty.current = false;
  };
  let deleteAddon = (addonId: string) => {
    setAddonsArray(addonsArray.filter((addon) => addon.id !== addonId));
    isDirty.current = true;
  };
  let confirmDelete = (addonId: string) => {
    const CANCEL = t('Cancel');
    showDialog(
      t('Delete Addon?'),
      t(
        'Warning: Deleting an addon will also delete the associated signup form field as well as related vendor responses to the question.',
      ),
      [CANCEL, t('Proceed')],
      async (value) => {
        if (value === CANCEL) {
          return;
        }
        deleteAddon(addonId);
      },
    );
  };

  if (isLoading) {
    return (
      <View style={styles.root}>
        <LoadingIndicator />
      </View>
    );
  }
  return (
    <View style={styles.root}>
      <ScrollView style={styles.scrollView}>
        <View style={[styles.row, styles.header]}>
          <View style={[styles.columnHeader, styles.resourceColumn]}>
            <Text style={styles.headerText}>{t('Resource Name')}</Text>
          </View>
          <View style={[styles.columnHeader, styles.priceColumn]}>
            <Text style={styles.headerText}>
              {t('Default Price (per date)')}
            </Text>
          </View>
        </View>
        {addonsArray.map((addon, index) => {
          return (
            <View key={addon.id} style={styles.row}>
              <View style={[styles.column, styles.resourceColumn]}>
                <TextInput
                  value={addon.name}
                  onChangeText={(value) => {
                    onChangeAddon({ ...addon, name: value });
                  }}
                />
              </View>
              <View style={[styles.column, styles.priceColumn]}>
                <CurrencyInput
                  disabled={!canEdit}
                  accountCurrency={account.settings?.currency ?? 'USD'}
                  allowNegative={true}
                  onChange={(value) => {
                    onChangeAddon({
                      ...addon,
                      defaultPrice: value,
                    });
                  }}
                  defaultValue={addon.defaultPrice}
                />
              </View>
              {index !== addonsArray.length - 1 && (
                <View style={[styles.column, styles.iconColumn]}>
                  <Link
                    onPress={() => {
                      confirmDelete(addon.id);
                    }}
                  >
                    <Icon name="remove" size="L" />
                  </Link>
                </View>
              )}
            </View>
          );
        })}
        <Separator style={{ marginVertical: 12 }} />
        {canEdit && isDirty.current && (
          <ButtonToolbar style={styles.toolbar}>
            <Button
              title={t('Revert Changes')}
              onPress={() => {
                revertChanges();
              }}
            />
            <Button
              isPrimary
              title={t('Save')}
              onPress={() => {
                commitSaveAddons();
              }}
            />
          </ButtonToolbar>
        )}
      </ScrollView>
    </View>
  );
}

let styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: themeColors.white,
    borderRadius: 5,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: themeColors.withOpacity.blue50,
  },
  row: {
    flexDirection: 'row',
  },
  columnHeader: {
    flexDirection: 'column',
    padding: 8,
  },
  headerText: { fontSize: 20, fontWeight: '500' },
  column: {
    flexDirection: 'column',
    padding: 8,
  },
  resourceColumn: {
    width: '70%',
  },
  priceColumn: {
    width: '25%',
  },
  iconColumn: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    flexDirection: 'row',
  },
  scrollView: {
    flex: 1,
    padding: 24,
  },
  toolbar: {
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingVertical: 10,
  },
});

export default AddonsList;
